
import Vue from 'vue';

import DeviceMap from '@/components/configuration/DeviceMap.vue';
import TreeView from '@/components/dashboard/TreeView.vue';
import { Device, Floor } from '@/interfaces/locations';
import { Room, Zone } from '@/interfaces/lighting';
import { LocationTree, TreeButton } from '@/interfaces/locationTree';
import permissions from '@/lib/permissions';
import { DeviceType } from '@/interfaces/types';
import HeatMap from '@/components/configuration/HeatMap.vue';
import { Events } from '@/interfaces/events';
import dayjs from '@/plugins/dayjs';
import { AppliedRule, Rule } from '@/interfaces/rules';
import { AxiosError } from 'axios';
import { Controller } from '@/interfaces/controllers';
/*
This section is picked up by vue-i18n. DO NOT REMOVE.

	this.$t('Select luminaire');
	this.$t('Select switch');
*/

type Category = 'rooms' | 'zones' | 'devices' | 'gateways' | 'deviceCenter';

interface StartEndTime {
	from: dayjs.Dayjs;
	to: dayjs.Dayjs;
}

interface ComponentData {
	selectedFloor: Floor | null;
	selectedMapItem: { category: Category; id: number } | null;
	editMode: boolean;
	loadingHeatMap: boolean;
	time: StartEndTime;
}

type StatefulDevice = Device & { selected: boolean };

export default Vue.extend({
	components: {
		HeatMap,
		DeviceMap,
		TreeView,
	},

	data(): ComponentData {
		return {
			selectedFloor: null,
			selectedMapItem: null,
			editMode: false,
			loadingHeatMap: false,
			time: {
				from: dayjs().utc(true).startOf('month'),
				to: dayjs().utc(true).endOf('month'),
			},
		};
	},

	computed: {
		floors(): Floor[] {
			return this.$store.getters['locations/floors/getFloors'];
		},

		controllers(): Controller[] {
			return this.$store.getters['controllers/controllers/getControllers'];
		},

		currentFloor(): Floor | undefined {
			if (
				this.selectedFloor &&
				this.floors.filter((floor) => floor === this.selectedFloor).length == 0
			) {
				this.changeFloor(null);
			}
			return this.selectedFloor ?? this.floors[0];
		},

		locale(): string {
			switch (this.$i18n.locale) {
				case 'en-gb':
					return 'en-GB';
				case 'fr':
					return 'fr-FR';
				default:
					return 'en-GB';
			}
		},

		rooms(): LocationTree[] {
			if (this.currentFloor) {
				return this.$store.getters['locations/rooms/getFloorRooms'](this.currentFloor.id)
					.sort((roomA: Room, roomB: Room) => {
						const nameA = roomA.name.toUpperCase();
						const nameB = roomB.name.toUpperCase();
						if (nameA < nameB) {
							return -1;
						}
						if (nameA > nameB) {
							return 1;
						}
						return 0;
					})
					.map(this.getRoomTree);
			}
			return [];
		},

		deviceTypes(): DeviceType[] {
			return this.$store.getters['types/deviceTypes/getDeviceTypes'];
		},

		luminaires(): StatefulDevice[] {
			return this.$store.getters['devices/luminaires/getLuminaires'].map(
				(l: Device): StatefulDevice => {
					return { ...l, selected: this.isIndirectlyHighlighted(l) };
				}
			);
		},

		switches(): StatefulDevice[] {
			const manualSwitchId: number | undefined = this.deviceTypes.find(
				(type) => type.name === 'Manual switch'
			)?.id;
			if (manualSwitchId) {
				return this.$store.getters['devices/switches/getCertainTypeSwitch'](manualSwitchId).map(
					(l: Device): StatefulDevice => {
						return { ...l, selected: this.isIndirectlyHighlighted(l) };
					}
				);
			}
			return [];
		},

		gateways(): StatefulDevice[] {
			return this.$store.getters['devices/gateways/getGateways'].map(
				(l: Device): StatefulDevice => {
					return { ...l, selected: this.isIndirectlyHighlighted(l) };
				}
			);
		},

		gatewaysWithoutFloor(): StatefulDevice[] {
			return this.gateways.filter((g) => !g.fk_area);
		},

		floorGateways(): StatefulDevice[] {
			if (this.currentFloor) {
				return this.gateways.filter((g) => this.currentFloor!.id === g.fk_area);
			}
			return [];
		},

		floorLuminaries(): StatefulDevice[] {
			if (this.currentFloor) {
				return this.luminaires.filter((lum) => this.currentFloor!.id === lum.fk_area);
			}
			return [];
		},

		floorSwitches(): StatefulDevice[] {
			if (this.currentFloor) {
				return this.switches.filter((sw) => sw.fk_area === this.currentFloor!.id);
			}
			return [];
		},

		floorLuminairesWithCoordinates(): StatefulDevice[] {
			return this.floorLuminaries.filter(
				(luminaire) => !(luminaire.local_latitude === null && luminaire.local_longitude === null)
			);
		},

		gatewayList(): LocationTree[] {
			return [...this.floorGateways, ...this.gatewaysWithoutFloor]
				.slice()
				.sort((gatewayA: Device, gatewayB: Device) => {
					const nameA = gatewayA.name.toUpperCase();
					const nameB = gatewayB.name.toUpperCase();
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				})
				.map(this.getGatewayList);
		},

		devices(): StatefulDevice[] {
			return [...this.luminaires, ...this.switches, ...this.gateways];
		},

		floorDevices(): StatefulDevice[] {
			return [
				...this.floorLuminaries,
				...this.floorSwitches,
				...this.floorGateways,
				...this.gatewaysWithoutFloor,
			];
		},

		occupancyEvents(): { [id: number]: Events[] } {
			return this.$store.getters['events/occupancy/getOccupancies'];
		},

		userCanReadLuminaires(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'luminaires',
				'read',
			]);
		},

		userCanWriteLuminaires(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'luminaires',
				'write',
			]);
		},

		unChangeableLuminaires(): Device[] {
			return this.$store.getters['devices/luminaires/getLuminaires'];
		},

		unChangeableSwitches(): Device[] {
			return this.$store.getters['devices/switches/getSwitches'];
		},

		unChangeableGateways(): Device[] {
			return this.$store.getters['devices/gateways/getGateways'];
		},

		identifyRule(): Rule | undefined {
			return (
				this.$store.getters['activities/rules/getRulesCertainType']('lighting_override') as Rule[]
			).find((rule) => rule.name === 'Lighting identify override rule');
		},

		offRule(): Rule | undefined {
			return (
				this.$store.getters['activities/rules/getRulesCertainType']('lighting_override') as Rule[]
			).find((rule) => rule.name === 'Lighting off override rule');
		},
	},

	methods: {
		changeFloor(floor: Floor | null, special = 'none'): void {
			this.selectedFloor = floor;
			switch (special) {
				case 'heat':
					this.fetchOccupanyEvents();
					break;
				default:
					break;
			}
		},

		async saveCoordinates(
			luminaires: Device[],
			switches: Device[],
			gateways: Device[]
		): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				for (const luminaire of luminaires) {
					await this.$store.dispatch('devices/luminaires/modifyLuminaire', {
						id: luminaire.id,
						payload: {
							local_latitude: luminaire.local_latitude,
							local_longitude: luminaire.local_longitude,
						},
					});
				}
				for (const _switch of switches) {
					await this.$store.dispatch('devices/switches/modifySwitch', {
						id: _switch.id,
						payload: {
							local_latitude: _switch.local_latitude,
							local_longitude: _switch.local_longitude,
						},
					});
				}

				for (const gateway of gateways) {
					await this.$store.dispatch('devices/gateways/modifyGateway', {
						id: gateway.id,
						payload: {
							local_latitude: gateway.local_latitude,
							local_longitude: gateway.local_longitude,
						},
					});
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-save-coordinates',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		getRoomTree(room: Room): LocationTree {
			const category = 'rooms';
			const zones: Zone[] = this.$store.getters['locations/zones/getRoomZones'](room.id).sort(
				(zoneA: Zone, zoneB: Zone) => {
					const nameA = zoneA.name.toUpperCase();
					const nameB = zoneB.name.toUpperCase();
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				}
			);
			return {
				id: room.id,
				name: room.name,
				category,
				expanded: false,
				nodes: zones.map(this.getZoneTree),
				buttons: [
					{
						buttonName: this.$t('Select room').toString(),
						buttonColor: 'info',
						category,
						disable: !this.editMode,
					},
				],
				highlighted: this.isHighlighted(category, room.id),
			};
		},

		getZoneTree(zone: Zone): LocationTree {
			const category = 'zones';
			const devices = this.devices
				.filter((l: Device) => zone.devices.includes(l.id))
				.sort((deviceA: Device, deviceB: Device) => {
					const nameA = deviceA.name.toUpperCase();
					const nameB = deviceB.name.toUpperCase();
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				});
			return {
				id: zone.id,
				name: zone.name,
				category,
				expanded: false,
				nodes: devices.map(this.getDeviceTree),
				buttons: [
					{
						buttonName: this.$t('Select zone').toString(),
						buttonColor: 'info',
						category,
						disable: !this.editMode,
					},
				],
				highlighted: this.isHighlighted(category, zone.id),
			};
		},

		getDeviceTree(device: Device): LocationTree {
			const category = 'devices';
			const controller: Controller | undefined = this.$store.getters[
				'controllers/controllers/getController'
			](device.fk_controller);
			const buttons: TreeButton[] = [];
			if (this.editMode && device.local_latitude !== null && device.local_longitude !== null) {
				buttons.push({
					buttonName: this.$t('Center').toString(),
					buttonColor: 'info',
					category: 'deviceCenter',
				});
			}
			if (device.local_latitude !== null && device.local_longitude !== null) {
				let buttonText = `Select ${
					device.is_luminaire ? 'luminaire' : device.category == 'switches' ? 'switch' : 'sensor'
				}`;
				buttons.push({
					buttonName: this.$t(buttonText).toString(),
					buttonColor: 'info',
					category,
					disable: !this.editMode,
				});
			} else {
				buttons.push({
					buttonName: this.$t('Not added to map').toString(),
					buttonColor: 'info',
					category,
					disable: false,
				});
			}
			return {
				id: device.id,
				guid: controller?.guid,
				name: device.name,
				category,
				expanded: false,
				...(this.identifyRule &&
					device.is_luminaire && {
						identify: {
							status: !!this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
								device.id,
								this.identifyRule.id
							),
							disable: this.userCanWriteLuminaires,
						},
					}),
				...(this.offRule &&
					device.is_luminaire && {
						off: {
							status: !!this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
								device.id,
								this.offRule.id
							),
							disable: this.userCanWriteLuminaires,
						},
					}),
				channel: controller?.radio_channel,
				software_version: controller?.software_version,
				buttons,
				highlighted: this.isHighlighted(category, device.id),
			};
		},

		getGatewayList(device: Device): LocationTree {
			const category = 'gateways';
			const controller = this.$store.getters['controllers/controllers/getController'](
				device.fk_controller
			);
			const buttons: TreeButton[] = [];
			if (this.editMode && device.local_latitude !== null && device.local_longitude !== null) {
				buttons.push({
					buttonName: this.$t('Center').toString(),
					buttonColor: 'info',
					category: 'deviceCenter',
				});
			}
			if (device.local_latitude !== null && device.local_longitude !== null) {
				buttons.push({
					buttonName: this.$t('Select gateway').toString(),
					buttonColor: 'info',
					category,
					disable: !this.editMode,
				});
			} else {
				buttons.push({
					buttonName: this.$t('Not added to map').toString(),
					buttonColor: 'info',
					category,
					disable: false,
				});
			}
			return {
				id: device.id,
				guid: controller?.guid,
				name: device.name,
				category,
				expanded: false,
				channel: controller?.radio_channel,
				software_version: controller?.software_version,
				buttons,
				highlighted: this.isHighlighted(category, device.id),
			};
		},

		isHighlighted(category: Category, id: number): boolean {
			return this.selectedMapItem?.category === category && this.selectedMapItem?.id === id;
		},

		isIndirectlyHighlighted(device: Device): boolean {
			if (this.selectedMapItem !== null) {
				if (!this.isHighlighted('devices', device.id)) {
					if (this.selectedMapItem.category === 'zones') {
						const zone = this.$store.getters['locations/zones/getZone'](this.selectedMapItem.id);
						return zone?.devices.includes(device.id) ?? false;
					} else if (this.selectedMapItem.category === 'rooms') {
						const room = this.$store.getters['locations/rooms/getRoom'](this.selectedMapItem.id);
						const zones: Zone[] = this.$store.getters['locations/zones/getRoomZones'](room?.id);
						return zones.some((z: Zone) => z.devices.includes(device.id));
					} else if (this.selectedMapItem.category === 'gateways') {
						const gateway = this.$store.getters['devices/gateways/getGateway'](
							this.selectedMapItem.id
						);
						return gateway.id === device.id ?? false;
					}
					return false;
				}
				return true;
			}
			return false;
		},

		onDeviceSelected(id: number, category: 'devices' | 'gateways'): void {
			if (this.selectedMapItem && this.selectedMapItem.id === id) {
				this.selectedMapItem = null;
			} else {
				this.selectedMapItem = { category, id };
			}
		},

		onTreeButtonClick(category: Category, name: string, id: number): void {
			if (category === 'deviceCenter') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.deviceMapRef as any)!.resetDeviceLocation(id);
				return;
			}
			if (
				this.selectedMapItem &&
				this.selectedMapItem.id === id &&
				this.selectedMapItem.category === category
			) {
				this.selectedMapItem = null;
			} else {
				this.selectedMapItem = { category, id };
			}
		},

		changeTime(time: StartEndTime): void {
			this.time.from = time.from;
			this.time.to = time.to;
			this.fetchOccupanyEvents();
		},

		changeEditMode(status: boolean): void {
			if (status) {
				this.selectedMapItem = null;
			}
			this.editMode = status;
		},

		async fetchOccupanyEvents(): Promise<void> {
			this.loadingHeatMap = true;
			const promises: Promise<void>[] = [];
			const devices: number[][] = [];
			const from = this.time.from;
			const to = this.time.to;
			await this.$store.dispatch('events/occupancy/cleanAllOccupancies');
			for (const luminaire of this.floorLuminairesWithCoordinates.filter(
				(luminaire) => luminaire.is_movement_sensor
			)) {
				const zoneId: number | undefined = this.$store.getters['locations/zones/getDeviceZoneId'](
					luminaire.id
				);

				if (zoneId) {
					devices.push([luminaire.id, zoneId]);
				}
			}
			promises.push(
				this.$store.dispatch('events/occupancy/fetchOccupancies', {
					params: {
						devices: devices,
						start: `${from.year()}-${
							from.month() + 1
						}-${from.date()} ${from.hour()}:${from.minute()}:${from.second()}`,
						end: `${to.year()}-${
							to.month() + 1
						}-${to.date()} ${to.hour()}:${to.minute()}:${to.second()}`,
					},
				})
			);
			try {
				await Promise.all(promises);
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-occupancy-fetch',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.loadingHeatMap = false;
		},

		async changeOff(id: number, off: boolean): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.removeAppliedRule(id, 'identify');
				if (off) {
					await this.$store.dispatch('activities/appliedRules/addAppliedRule', {
						params: {
							fk_rule: this.offRule!.id,
							fk_device: id,
						},
					});
				} else {
					await this.removeAppliedRule(id, 'off');
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-change-off',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async changeIdentify(id: number, identify: boolean): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.removeAppliedRule(id, 'off');
				if (identify) {
					await this.$store.dispatch('activities/appliedRules/addAppliedRule', {
						params: {
							fk_rule: this.identifyRule!.id,
							fk_device: id,
						},
					});
				} else {
					await this.removeAppliedRule(id, 'identify');
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-change-identify',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async removeAppliedRule(id: number, type: string): Promise<void> {
			let appliedRule: AppliedRule | undefined;
			switch (type) {
				case 'off':
					appliedRule = this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
						id,
						this.offRule!.id
					);
					break;
				case 'identify':
					appliedRule = this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
						id,
						this.identifyRule!.id
					);
					break;
				default:
					appliedRule = undefined;
			}
			if (appliedRule) {
				await this.$store.dispatch('activities/appliedRules/removeAppliedRule', {
					id: appliedRule.id,
				});
			}
		},
	},
});
